import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { listarEquipe } from '../../acesso';
import type { Eu, MembroDaEquipe } from '../../acesso';
import type { Sessao } from '../../auth';
import { ErroDeArea } from '../../design/Estados';
import { FotoDaConta } from '../../design/FotoDePerfil';
import { Icone } from '../../design/Icone';
import {
  diaCurto,
  lerSemana,
  lerSemanaSugerida,
  ligarOperador,
  REGRAS_DA_PREMIACAO,
  reais,
  reaisCurto,
  semanaCurta,
  vendasSemPessoaPendentes,
} from '../../premiacao';
import type { ChequeDaPessoa, ChequeDaSemana } from '../../premiacao';
import { linkDasVendas } from '../inovapay/link-das-vendas';
import { feitoNaPonte, perguntaDaPonte } from './confirmacoes';
import { PROGRAMAS, contas } from './conta-da-semana';
import type { Conta } from './conta-da-semana';
import { EscolherModelo, FolhasA4 } from './FolhasA4';
import type { ModeloDaFolha } from './FolhasA4';
import { VendasDaPessoa } from './VendasDaPessoa';
import './premiacao.css';
import './conferencia.css';
import { nomeDaSemana, semanaDoDia } from '../../design/semana';

/**
 * TELA 15 (parte C) — CONFERÊNCIA DA SEMANA. "Não é o Giro de Prateleira."
 *
 * O confronto do que foi LANÇADO no InovaPay com as vendas do HOS, que chegam SOZINHAS pela API do
 * HOS. A semana é de SÁBADO a SEXTA desde 24/10/2026 (até 15/10 era sexta a quinta; 16–23/10 foi a
 * transição) e abre na última fechada (a do papel de sábado).
 *
 * As regras que TIRAM o prêmio (a conta é do servidor, o motor do cheque — aqui só se mostra):
 *   · lançou e não vendeu (não há a venda no HOS)      → não paga
 *   · vendeu e não lançou                               → perde
 *   · lançou em dia diferente do da venda               → perde
 *   · pré-vencido vendido no dia em que o lote entrou   → perde (regra de 24/09/2026)
 *   · vendido abaixo do card                            → não premia
 *   · cancelada pelo administrador / devolvida          → desconta
 * Liberar na mão é na Premiação. Cancelar um lançamento é aqui, com motivo (o servidor recusa
 * semana já paga). A folha de pagamento imprime de um vendedor ou de todos de uma vez.
 */

type Filtro =
  | 'todos'
  | 'alerta'
  | 'nao_vendeu'
  | 'nao_lancou'
  | 'dia_errado'
  | 'lote_dia'
  | 'canceladas'
  | 'devolvidas'
  | 'aguardando';

const SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const temAlerta = (k: Conta) =>
  k.naoVendeu + k.naoLancou + k.diaErrado + k.loteDoDia + k.canceladas + k.devolvidas > 0;

const mais = (dia: string, n: number) =>
  new Date(Date.parse(`${dia}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const hojeEmMuriae = () =>
  new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
function haQuanto(iso: string | null): string {
  if (!iso) return 'nunca';
  const min = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${String(min)} min`;
  const h = Math.round(min / 60);
  return h < 48 ? `há ${String(h)} h` : `há ${String(Math.round(h / 24))} dias`;
}
const ehDia = (s: string | null): s is string => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);
const ehId = (s: string | null): s is string => !!s && /^[0-9a-f-]{36}$/.test(s);

export function Conferencia({
  sessao,
  eu,
  parametros,
}: {
  sessao: Sessao;
  eu: Eu | null;
  parametros?: URLSearchParams;
}) {
  const semanaPedida = parametros?.get('semana') ?? null;
  const pessoaPedida = parametros?.get('pessoa') ?? null;
  const [periodo, setPeriodo] = useState<{ de: string; ate: string } | null>(() =>
    ehDia(semanaPedida) ? semanaDoDia(semanaPedida) : null,
  );
  const [dados, setDados] = useState<ChequeDaSemana | null>(null);
  const [equipe, setEquipe] = useState<readonly MembroDaEquipe[]>([]);
  const [erro, setErro] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [aberto, setAberto] = useState<string | null>(ehId(pessoaPedida) ? pessoaPedida : null);
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [busca, setBusca] = useState('');
  const [verLigados, setVerLigados] = useState(false);
  const [verSemEfeito, setVerSemEfeito] = useState(false);
  // Quem é quem: o select só ESCOLHE; ligar pede confirmação e dá para desfazer (paga a pessoa)
  const [escolhas, setEscolhas] = useState<Readonly<Record<string, string>>>({});
  const [pedido, setPedido] = useState<{
    nomeHos: string;
    accountId: string | null;
    pessoa: string | null;
  } | null>(null);
  const [ligando, setLigando] = useState(false);
  const [feito, setFeito] = useState<{ nomeHos: string; texto: string } | null>(null);
  // quem imprimir: primeiro escolhe o modelo da folha (4 modelos), depois imprime
  const [escolher, setEscolher] = useState<readonly string[] | null>(null);
  const [imprimir, setImprimir] = useState<{
    readonly ids: readonly string[];
    readonly modelo: ModeloDaFolha;
  } | null>(null);
  const podeConferir = eu?.capacidades.includes('premiacao.conferir') ?? false;
  // o servidor só deixa o administrador cancelar lançamento de outra pessoa com `vendas.ver_todas`
  const podeCancelar = eu?.capacidades.includes('vendas.ver_todas') ?? false;

  useEffect(() => {
    if (!periodo)
      void lerSemanaSugerida().then((r) => {
        if (r.ok) setPeriodo(r.dados);
        else setErro(true);
      });
    void listarEquipe().then((r) => {
      if (r.ok) setEquipe(r.dados.pessoas.filter((p) => p.accountId && p.situacao === 'ativo'));
    });
    // só na abertura: a semana pedida no endereço vale uma vez
  }, []);

  const carregar = useCallback(
    (manter = false) => {
      if (!periodo) return;
      setErro(false);
      if (!manter) setDados(null);
      void lerSemana(periodo.de, periodo.ate).then((r) => {
        if (r.ok) setDados(r.dados);
        else setErro(true);
      });
    },
    [periodo],
  );
  useEffect(() => carregar(), [carregar]);

  // A semana escolhida vai para o endereço: recarregar, copiar o link ou voltar de outra tela
  // abre a MESMA semana. `replaceState` não dispara `hashchange` — a tela não remonta.
  useEffect(() => {
    if (!periodo) return;
    const [rota = '', consulta = ''] = window.location.hash.split('?');
    const q = new URLSearchParams(consulta);
    if (q.get('semana') === periodo.de) return;
    q.set('semana', periodo.de);
    window.history.replaceState(window.history.state, '', `${rota}?${q.toString()}`);
  }, [periodo]);

  // imprime a(s) folha(s) depois que a página delas está montada; ao terminar, some
  useEffect(() => {
    if (!imprimir) return;
    const fim = () => setImprimir(null);
    window.addEventListener('afterprint', fim);
    const t = window.setTimeout(() => window.print(), 80);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('afterprint', fim);
    };
  }, [imprimir]);

  const semRelatorio = useMemo(() => new Set(dados?.avisos.diasSemRelatorio ?? []), [dados]);
  const linhas = useMemo(
    () =>
      (dados?.pessoas ?? [])
        .map((p) => ({ p, k: contas(p, semRelatorio) }))
        .sort((a, b) => b.p.aReceber - a.p.aReceber || b.k.lancado - a.k.lancado),
    [dados, semRelatorio],
  );
  const total = linhas.reduce(
    (s, { k }) => {
      for (const c of Object.keys(s) as (keyof Conta)[]) s[c] += k[c];
      return s;
    },
    {
      lancado: 0,
      confirmado: 0,
      aguardando: 0,
      naoVendeu: 0,
      naoLancou: 0,
      diaErrado: 0,
      loteDoDia: 0,
      abaixo: 0,
      canceladas: 0,
      devolvidas: 0,
      perdeuReais: 0,
    },
  );
  const passa = (k: Conta): boolean => {
    switch (filtro) {
      case 'alerta':
        return temAlerta(k);
      case 'nao_vendeu':
        return k.naoVendeu > 0;
      case 'nao_lancou':
        return k.naoLancou > 0;
      case 'dia_errado':
        return k.diaErrado > 0;
      case 'lote_dia':
        return k.loteDoDia > 0;
      case 'canceladas':
        return k.canceladas > 0;
      case 'devolvidas':
        return k.devolvidas > 0;
      case 'aguardando':
        return k.aguardando > 0;
      default:
        return true;
    }
  };
  const visiveis = linhas.filter(({ p, k }) => {
    if (!passa(k)) return false;
    const b = busca.trim().toLowerCase();
    return !b || p.nome.toLowerCase().includes(b) || (p.codigo ?? '').includes(b);
  });

  const ligar = async (nomeHos: string, accountId: string | null, fora: boolean) => {
    setLigando(true);
    const r = await ligarOperador(sessao, nomeHos, accountId, fora);
    setLigando(false);
    if (r.ok) carregar(true);
    else setAviso(r.mensagem);
    return r.ok;
  };
  const confirmarPonte = async () => {
    if (!pedido) return;
    const ok = await ligar(pedido.nomeHos, pedido.accountId, pedido.accountId === null);
    if (ok)
      setFeito({ nomeHos: pedido.nomeHos, texto: feitoNaPonte(pedido.nomeHos, pedido.pessoa) });
    setPedido(null);
  };
  // Desfazer: o nome volta a "não mapeado" — sem pessoa, não paga ninguém até alguém confirmar.
  const desfazerPonte = async () => {
    if (!feito) return;
    if (await ligar(feito.nomeHos, null, false)) setFeito(null);
  };
  const nomeDaConta = (accountId: string) =>
    equipe.find((m) => m.accountId === accountId)?.nome ?? null;

  if (erro) return <ErroDeArea oQue="a conferência" aoTentar={() => carregar()} />;

  const hoje = hojeEmMuriae();
  const semanaAtual = semanaDoDia(hoje);
  const vendasSemPessoa = (nome: string) =>
    dados?.avisos.vendasSemPessoa.find((v) => v.nome === nome)?.vendas ?? 0;
  // Só pede uma pessoa quem VENDEU produto dos programas sem ninguém ligado. O resto (caixa,
  // usuário de sistema, quem não tem conta) não mexe na premiação desta semana: fica recolhido.
  const todosPendentes = dados ? [...dados.avisos.operadoresPendentes] : [];
  // nome que vendeu sem pessoa e ainda nem entrou na ponte também pede uma pessoa
  if (dados)
    for (const v of vendasSemPessoaPendentes(dados))
      if (!todosPendentes.some((o) => o.nomeHos === v.nome))
        todosPendentes.push({
          nomeHos: v.nome,
          palpite: null,
          palpiteId: null,
          confianca: 'sem palpite',
        });
  const pendentes = todosPendentes.filter((o) => vendasSemPessoa(o.nomeHos) > 0);
  const semEfeito = todosPendentes.filter((o) => vendasSemPessoa(o.nomeHos) === 0);
  const ligados = dados?.ponteAutomatica ?? [];
  const leitura = dados?.fonteDoHos?.ultimaLeituraDaApi ?? null;
  const apiAtrasada = leitura !== null && Date.now() - Date.parse(leitura) > 30 * 60_000;
  const porTipo = dados?.totais.porTipo;
  const totalProgramas = porTipo
    ? PROGRAMAS.reduce((s, x) => s + Math.max(0, porTipo[x.id].devido), 0)
    : 0;
  const pessoaAberta = dados?.pessoas.find((p) => p.pessoaId === aberto) ?? null;
  const perdeuPor = (f: (c: ChequeDaPessoa) => number) =>
    (dados?.pessoas ?? []).reduce((s, p) => s + f(p), 0);

  const REGRAS: readonly {
    id: Filtro;
    titulo: string;
    itens: number;
    valor: string;
    nota: string;
  }[] = [
    {
      id: 'nao_vendeu',
      titulo: 'Lançou e não vendeu',
      itens: total.naoVendeu,
      valor: reais(
        perdeuPor((p) =>
          p.linhas.naoVendeu
            .filter((l) => !semRelatorio.has(l.dia))
            .reduce((s, l) => s + l.pago, 0),
        ),
      ),
      nota: 'o app gravou, o HOS não vendeu: não paga',
    },
    {
      id: 'nao_lancou',
      titulo: 'Vendeu e não lançou',
      itens: total.naoLancou,
      valor: reais(perdeuPor((p) => p.linhas.naoLancou.reduce((s, l) => s + l.perdeu, 0))),
      nota: 'perdeu o prêmio da venda',
    },
    {
      id: 'dia_errado',
      titulo: 'Lançou em dia diferente',
      itens: total.diaErrado,
      valor: reais(perdeuPor((p) => p.linhas.diaErrado.reduce((s, l) => s + l.perdeu, 0))),
      nota: 'perde, salvo liberado na Premiação',
    },
    {
      id: 'lote_dia',
      titulo: 'Lote vendido no dia em que entrou',
      itens: total.loteDoDia,
      valor: reais(
        perdeuPor((p) => (p.linhas.loteDoMesmoDia ?? []).reduce((s, l) => s + l.perdeu, 0)),
      ),
      nota: 'pré-vencido: perde (desde 25/09)',
    },
    {
      id: 'canceladas',
      titulo: 'Canceladas',
      itens: total.canceladas,
      valor: reais(dados ? -dados.totais.cancelado : 0),
      nota: 'canceladas pelo administrador',
    },
    {
      id: 'devolvidas',
      titulo: 'Devolvidas',
      itens: total.devolvidas,
      valor: reais(dados ? -dados.totais.devolucao : 0),
      nota: 'devolução tira a comissão da venda',
    },
  ];

  return (
    <section className="pr cf">
      <div className="cf-cartao">
        <div className="cf-topo">
          <div>
            <div className="cf-titulo">
              <h1>Conferência da semana</h1>
              <span className="cf-selo">NÃO É O GIRO DE PRATELEIRA</span>
            </div>
            <div className="pr-sub">
              {periodo
                ? `Semana ${semanaCurta(periodo.de, periodo.ate)} · ${nomeDaSemana(periodo)}`
                : 'Carregando…'}{' '}
              · o que o InovaPay registrou contra as vendas do HOS
            </div>
          </div>
          <div className="pr-acoes">
            {periodo && (
              <div className="cf-navega" role="group" aria-label="Semana">
                <button
                  type="button"
                  aria-label="Semana anterior"
                  onClick={() => setPeriodo(semanaDoDia(mais(periodo.de, -1)))}
                >
                  <span className="cf-seta-esq">
                    <Icone nome="chev-r" tamanho={14} traco={1.8} />
                  </span>
                </button>
                <strong>{semanaCurta(periodo.de, periodo.ate)}</strong>
                <button
                  type="button"
                  aria-label="Próxima semana"
                  disabled={periodo.de >= semanaAtual.de}
                  onClick={() => setPeriodo(semanaDoDia(mais(periodo.ate, 1)))}
                >
                  <Icone nome="chev-r" tamanho={14} traco={1.8} />
                </button>
                {periodo.de !== semanaAtual.de && (
                  <button
                    type="button"
                    className="cf-navega-hoje"
                    onClick={() => setPeriodo(semanaAtual)}
                  >
                    Semana atual
                  </button>
                )}
              </div>
            )}
            <button
              type="button"
              className="pr-botao"
              disabled={!dados || visiveis.length === 0}
              onClick={() => setEscolher(visiveis.map(({ p }) => p.pessoaId))}
              title="Folhas A4 na ordem da tabela (respeita o filtro): escolha entre 4 modelos"
            >
              <Icone nome="file" tamanho={13} traco={1.8} /> Imprimir folhas ({visiveis.length})
            </button>
            <a
              className="pr-botao"
              href={periodo ? `#/premiacao?semana=${periodo.de}` : '#/premiacao'}
            >
              Premiação
            </a>
          </div>
        </div>

        <div className={`cf-fonte-hos${apiAtrasada ? ' cf-fonte-atrasada' : ''}`} role="status">
          <span className="cf-ponto" />
          {leitura ? (
            <>
              Vendas, cancelamentos e devoluções chegam sozinhos pela <strong>API do HOS</strong> ·
              última leitura {haQuanto(leitura)}
              {apiAtrasada ? ' — atrasada: o sincronizador pode estar parado' : ''}
            </>
          ) : (
            <>A API do HOS ainda não fez nenhuma leitura: os dias abaixo vieram da cópia do 1.0.</>
          )}
        </div>

        {aviso && (
          <div className="pr-recusado" role="alert">
            {aviso}
            <button type="button" className="cf-fechar-aviso" onClick={() => setAviso(null)}>
              ok
            </button>
          </div>
        )}
        {pedido && (
          <div className="pr-modal-fundo" role="dialog" aria-label="Quem é quem no HOS">
            <div className="pr-modal">
              <h3>{perguntaDaPonte(pedido.nomeHos, pedido.pessoa)}</h3>
              <p>
                {pedido.pessoa
                  ? `As vendas de ${pedido.nomeHos} no HOS passam a pagar ${pedido.pessoa}.`
                  : 'Use para caixa, usuário de sistema ou quem não tem conta no InovaPay.'}{' '}
                Dá para desfazer logo depois.
              </p>
              <div className="pr-modal-acoes">
                <button
                  type="button"
                  className="pr-botao"
                  onClick={() => setPedido(null)}
                  disabled={ligando}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="pr-botao pr-botao-vermelho"
                  disabled={ligando}
                  onClick={() => void confirmarPonte()}
                  autoFocus
                >
                  {ligando ? 'Gravando…' : pedido.pessoa ? 'Ligar' : 'Não é balconista'}
                </button>
              </div>
            </div>
          </div>
        )}

        {dados?.fonteDoHos && (
          <div
            className="cf-dias"
            aria-label="Dias da semana"
            style={{ '--dias': dados.fonteDoHos.dias.length } as CSSProperties}
          >
            {dados.fonteDoHos.dias.map((d) => {
              const dow = SEMANA[new Date(`${d.dia}T12:00:00Z`).getUTCDay()] ?? '';
              const estado =
                d.situacao === 'futuro'
                  ? 'futuro'
                  : d.situacao === 'em_andamento'
                    ? 'hoje'
                    : d.fonte === 'api'
                      ? 'api'
                      : d.fonte === 'relatorio'
                        ? 'antigo'
                        : 'vazio';
              return (
                <div key={d.dia} className={`cf-dia cf-dia-${estado}`}>
                  <div className="cf-dia-topo">
                    <span>{dow}</span>
                    <span className="cf-dia-data">{diaCurto(d.dia)}</span>
                  </div>
                  {d.situacao === 'futuro' ? (
                    <div className="cf-dia-vazio">ainda não chegou</div>
                  ) : (
                    <>
                      <div className="cf-dia-valor">
                        {d.valor === null ? 'Vendas lidas' : reaisCurto(Number(d.valor))}
                      </div>
                      <div className="cf-dia-sub">
                        {d.cupons === null ? '' : `${d.cupons.toLocaleString('pt-BR')} cupons`}
                        {d.devolucoes ? ` · ${String(d.devolucoes)} dev.` : ''}
                      </div>
                      <div className="cf-dia-fonte">
                        {estado === 'hoje'
                          ? 'em andamento'
                          : estado === 'api'
                            ? 'API do HOS'
                            : estado === 'antigo'
                              ? 'cópia do 1.0'
                              : 'sem venda'}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="cf-kpis cf-kpis-6">
          <div className="cf-kpi">
            <div className="pr-kpi-rotulo">LANÇADO NO INOVAPAY</div>
            <div className="cf-kpi-valor">
              {dados ? total.lancado.toLocaleString('pt-BR') : '—'}
            </div>
            <div className="pr-kpi-sub">itens na semana</div>
          </div>
          <div className="cf-kpi">
            <div className="pr-kpi-rotulo">
              CONFIRMADO NO HOS <span className="cf-fonte">API</span>
            </div>
            <div className="cf-kpi-valor cf-verde">
              {dados ? total.confirmado.toLocaleString('pt-BR') : '—'}
            </div>
            <div className="pr-kpi-sub">
              {dados && total.lancado > 0
                ? `${((total.confirmado / total.lancado) * 100).toFixed(1).replace('.', ',')}% do lançado`
                : ' '}
            </div>
          </div>
          <div className="cf-kpi">
            <div className="pr-kpi-rotulo">AGUARDANDO O HOS</div>
            <div className="cf-kpi-valor">{dados ? total.aguardando : '—'}</div>
            <div className="pr-kpi-sub">lançados hoje ou em dia sem venda do HOS</div>
          </div>
          <div className={`cf-kpi${total.perdeuReais > 0 ? ' cf-kpi-alerta' : ''}`}>
            <div className="pr-kpi-rotulo">PERDEU PELAS REGRAS</div>
            <div className="cf-kpi-valor">{dados ? reais(total.perdeuReais) : '—'}</div>
            <div className="pr-kpi-sub">não lançou, dia diferente, lote do dia</div>
          </div>
          <div className="cf-kpi">
            <div className="pr-kpi-rotulo">DESCONTOS</div>
            <div className="cf-kpi-valor">
              {dados ? reais(-(dados.totais.devolucao + dados.totais.cancelado)) : '—'}
            </div>
            <div className="pr-kpi-sub">devoluções e canceladas</div>
          </div>
          <div className="cf-kpi">
            <div className="pr-kpi-rotulo">PREMIAÇÃO APURADA</div>
            <div className="cf-kpi-valor cf-ouro">
              {dados ? reaisCurto(dados.totais.aReceber) : '—'}
            </div>
            <div className="pr-kpi-sub">já descontado o deduzido</div>
          </div>
        </div>

        {dados && (
          <div className="cf-regras" aria-label="O que tirou prêmio nesta semana">
            {REGRAS.map((r) => (
              <button
                key={r.id}
                type="button"
                className={`cf-regra${r.itens > 0 ? ' cf-regra-tem' : ''}${filtro === r.id ? ' cf-regra-ativa' : ''}`}
                aria-pressed={filtro === r.id}
                onClick={() => setFiltro(filtro === r.id ? 'todos' : r.id)}
              >
                <span className="cf-regra-titulo">{r.titulo}</span>
                <strong>
                  {r.itens} {r.itens === 1 ? 'item' : 'itens'}
                </strong>
                <span className="cf-regra-valor">{r.itens > 0 ? r.valor : '—'}</span>
                <em>{r.nota}</em>
              </button>
            ))}
          </div>
        )}

        {porTipo && totalProgramas > 0 && (
          <div className="cf-programas" aria-label="Premiação por programa">
            <div className="cf-barra">
              {PROGRAMAS.map((x) => {
                const v = Math.max(0, porTipo[x.id].devido);
                return v > 0 ? (
                  <span
                    key={x.id}
                    className={`cf-barra-${x.id}`}
                    style={{ width: `${String((v / totalProgramas) * 100)}%` }}
                  />
                ) : null;
              })}
            </div>
            <div className="cf-legenda">
              {PROGRAMAS.map((x) => (
                <span key={x.id}>
                  <i className={`cf-barra-${x.id}`} />
                  {x.nome} <strong>{reais(porTipo[x.id].devido)}</strong>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="cf-filtros">
          {(
            [
              ['todos', 'Todos'],
              ['alerta', 'Com alguma pendência'],
              ['aguardando', 'Aguardando o HOS'],
            ] as const
          ).map(([id, rotulo]) => (
            <button
              key={id}
              type="button"
              className={`cf-chip${filtro === id ? ' cf-chip-ativo' : ''}`}
              aria-pressed={filtro === id}
              onClick={() => setFiltro(id)}
            >
              {rotulo}
            </button>
          ))}
          {!['todos', 'alerta', 'aguardando'].includes(filtro) && (
            <button
              type="button"
              className="cf-chip cf-chip-ativo"
              onClick={() => setFiltro('todos')}
            >
              {REGRAS.find((r) => r.id === filtro)?.titulo} ✕
            </button>
          )}
          <input
            className="cf-busca"
            type="search"
            placeholder="Buscar balconista ou código"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            aria-label="Buscar balconista"
          />
        </div>

        <div className="pr-tabela cf-tabela">
          <div className="pr-rolagem">
            <table>
              <thead>
                <tr>
                  <th className="pr-esq">BALCONISTA</th>
                  {PROGRAMAS.map((x) => (
                    <th key={x.id}>{x.nome.toUpperCase()}</th>
                  ))}
                  <th>LANÇADO</th>
                  <th>CONFIRMADO</th>
                  <th title="Lançou e não vendeu">NÃO VENDEU</th>
                  <th title="Vendeu e não lançou">NÃO LANÇOU</th>
                  <th title="Lançou em dia diferente da venda">DIA ERRADO</th>
                  <th title="Pré-vencido vendido no dia em que o lote entrou">LOTE DO DIA</th>
                  <th>CANCEL.</th>
                  <th>DEVOLUÇÃO</th>
                  <th>PREMIAÇÃO</th>
                  <th className="pr-esq">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {dados === null && (
                  <tr>
                    <td colSpan={15} className="pr-vazio">
                      Carregando a semana…
                    </td>
                  </tr>
                )}
                {dados !== null && visiveis.length === 0 && (
                  <tr>
                    <td colSpan={15} className="pr-vazio">
                      {linhas.length === 0
                        ? 'Nenhum lançamento nem venda do HOS nesta semana.'
                        : 'Ninguém com esse filtro.'}
                    </td>
                  </tr>
                )}
                {visiveis.map(({ p, k }) => {
                  const n = (v: number) => (
                    <td className={v > 0 ? 'pr-vermelho' : 'cf-zero'}>{v > 0 ? v : '—'}</td>
                  );
                  const pago = dados?.pagamentos[p.pessoaId];
                  return (
                    <tr
                      key={p.pessoaId}
                      className={`cf-clicavel${temAlerta(k) ? ' cf-linha-alerta' : ''}`}
                      tabIndex={0}
                      onClick={() => setAberto(p.pessoaId)}
                      onKeyDown={(e) =>
                        (e.key === 'Enter' || e.key === ' ') && setAberto(p.pessoaId)
                      }
                    >
                      <td className="pr-esq pr-nome">
                        {p.nome}
                        <span className="cf-cod">cód {p.codigo ?? '—'}</span>
                      </td>
                      {PROGRAMAS.map((x) => (
                        <td key={x.id} className={p.tipos[x.id].devido ? '' : 'cf-zero'}>
                          {p.tipos[x.id].devido ? reaisCurto(p.tipos[x.id].devido) : '—'}
                        </td>
                      ))}
                      <td>{k.lancado}</td>
                      <td className="cf-verde">{k.confirmado}</td>
                      {n(k.naoVendeu)}
                      {n(k.naoLancou)}
                      {n(k.diaErrado)}
                      {n(k.loteDoDia)}
                      {n(k.canceladas)}
                      <td className={p.devolucao ? 'pr-vermelho' : 'cf-zero'}>
                        {p.devolucao ? reais(-p.devolucao) : '—'}
                      </td>
                      <td className="pr-total">{reaisCurto(p.aReceber)}</td>
                      <td className="pr-esq">
                        {pago && !pago.estornado ? (
                          <span className="pr-status pr-status-pago">
                            <Icone nome="check" tamanho={12} traco={2} /> Pago
                          </span>
                        ) : temAlerta(k) ? (
                          <span className="cf-status-alerta">
                            <Icone nome="warn" tamanho={12} traco={1.8} /> Conferir
                          </span>
                        ) : k.aguardando > 0 ? (
                          <span className="pr-status pr-status-apuracao">Aguardando o HOS</span>
                        ) : (
                          <span className="pr-status pr-status-pago">
                            <Icone nome="check" tamanho={12} traco={2} /> Conferido
                          </span>
                        )}
                        {p.zeradoPorMeta && <span className="cf-meta">meta não batida</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="pr-tabela-rodape cf-rodape">
            <span>
              {dados
                ? `${String(visiveis.length)} de ${String(linhas.length)} pessoas · clique para ver tudo do vendedor`
                : ''}
            </span>
            <span>item que não bate com o HOS não entra no cheque</span>
          </div>
        </div>
      </div>

      {dados && (
        <div className="cf-grade">
          <section className="cf-cartao cf-ponte">
            <h2>Quem é quem no HOS</h2>
            <p>
              A venda do HOS traz o código de quem vendeu, que é o login da pessoa no InovaPay: a
              ligação é feita sozinha por esse código. Nunca por semelhança de nome.
            </p>
            {feito && (
              <div className="cf-feito" role="status">
                <Icone nome="check" tamanho={13} traco={2} />
                <span>{feito.texto}</span>
                <button type="button" disabled={ligando} onClick={() => void desfazerPonte()}>
                  {ligando ? 'Desfazendo…' : 'Desfazer'}
                </button>
              </div>
            )}
            {ligados.length > 0 && (
              <div className="cf-ligados">
                <button
                  type="button"
                  className="cf-ligados-botao"
                  onClick={() => setVerLigados((v) => !v)}
                >
                  <Icone nome="check" tamanho={13} traco={2} />
                  {ligados.filter((l) => !l.sistema).length} ligado(s) pelo código
                  {ligados.some((l) => l.sistema)
                    ? ` · ${String(ligados.filter((l) => l.sistema).length)} usuário(s) de sistema fora da conta`
                    : ''}
                  <span className={verLigados ? 'cf-seta-cima' : 'cf-seta-baixo'}>
                    <Icone nome="chev-r" tamanho={12} traco={1.8} />
                  </span>
                </button>
                {verLigados && (
                  <ul>
                    {ligados.map((l) => (
                      <li key={l.nomeHos}>
                        <div>
                          <strong>{l.nomeHos}</strong>
                          <span>
                            código {l.codigo} no HOS →{' '}
                            {l.sistema ? 'usuário de sistema, não é balconista' : l.pessoaNome}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {pendentes.length === 0 ? (
              <p className="cf-tudo-certo">
                <Icone nome="check" tamanho={14} traco={2} /> Toda venda de programa desta semana
                tem pessoa ligada.
              </p>
            ) : (
              <>
                <div className="cf-subtitulo">Precisam de uma pessoa ({pendentes.length})</div>
                <ul>
                  {pendentes.map((o) => {
                    const vendas = vendasSemPessoa(o.nomeHos);
                    return (
                      <li key={o.nomeHos}>
                        <div>
                          <strong>{o.nomeHos}</strong>
                          <span>
                            {vendas} {vendas === 1 ? 'venda' : 'vendas'} sem pessoa
                            {o.palpite ? ` · parece ser ${o.palpite} (${o.confianca})` : ''}
                          </span>
                        </div>
                        {podeConferir && (
                          <div className="cf-ponte-acoes">
                            <select
                              value={escolhas[o.nomeHos] ?? o.palpiteId ?? ''}
                              aria-label={`Quem é ${o.nomeHos}`}
                              disabled={ligando}
                              onChange={(e) =>
                                setEscolhas((x) => ({ ...x, [o.nomeHos]: e.target.value }))
                              }
                            >
                              <option value="">Escolher pessoa…</option>
                              {equipe.map((m) => (
                                <option key={m.accountId ?? m.personId} value={m.accountId ?? ''}>
                                  {m.nome} · cód {m.login ?? '—'}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              disabled={ligando || !(escolhas[o.nomeHos] ?? o.palpiteId)}
                              onClick={() => {
                                const id = escolhas[o.nomeHos] ?? o.palpiteId;
                                if (id)
                                  setPedido({
                                    nomeHos: o.nomeHos,
                                    accountId: id,
                                    pessoa: nomeDaConta(id) ?? o.palpite,
                                  });
                              }}
                            >
                              Ligar
                            </button>
                            <button
                              type="button"
                              className="cf-fora"
                              disabled={ligando}
                              onClick={() =>
                                setPedido({ nomeHos: o.nomeHos, accountId: null, pessoa: null })
                              }
                            >
                              Não é balconista
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
            {semEfeito.length > 0 && (
              <div className="cf-ligados">
                <button
                  type="button"
                  className="cf-ligados-botao cf-ligados-neutro"
                  onClick={() => setVerSemEfeito((v) => !v)}
                >
                  {semEfeito.length} nome(s) do HOS sem pessoa, sem venda de programa nesta semana
                  <span className={verSemEfeito ? 'cf-seta-cima' : 'cf-seta-baixo'}>
                    <Icone nome="chev-r" tamanho={12} traco={1.8} />
                  </span>
                </button>
                {verSemEfeito && (
                  <ul>
                    {semEfeito.map((o) => (
                      <li key={o.nomeHos}>
                        <div>
                          <strong>{o.nomeHos}</strong>
                          <span>caixa, usuário de sistema ou quem não tem conta no InovaPay</span>
                        </div>
                        {podeConferir && (
                          <div className="cf-ponte-acoes">
                            <button
                              type="button"
                              className="cf-fora"
                              disabled={ligando}
                              onClick={() =>
                                setPedido({ nomeHos: o.nomeHos, accountId: null, pessoa: null })
                              }
                            >
                              Não é balconista
                            </button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {dados.avisos.ponteContradita.map((x) => (
              <div key={x.nomeHos} className="pr-incerto">
                A devolução desmente a ligação: <strong>{x.nomeHos}</strong> está ligado a{' '}
                {x.ponteNome}, mas as devoluções vêm com o código {x.devolucaoCodigo} (
                {x.devolucaoNome}). Confira.
              </div>
            ))}
          </section>

          <section className="cf-cartao cf-avisos">
            <h2>O que não pode passar calado</h2>
            <ul>
              <Aviso
                ok={dados.avisos.diasSemRelatorio.length === 0}
                texto={
                  dados.avisos.diasSemRelatorio.length === 0
                    ? 'Venda do HOS em todos os dias da semana.'
                    : `Sem venda do HOS: ${dados.avisos.diasSemRelatorio.map(diaCurto).join(', ')}. O que foi lançado nesses dias aguarda.`
                }
              />
              <Aviso
                ok={dados.emenda.buraco.length === 0 && dados.emenda.sobreposicao.length === 0}
                texto={
                  dados.emenda.buraco.length > 0
                    ? `Buraco desde a última semana paga: ${String(dados.emenda.buraco.length)} dia(s) (${diaCurto(dados.emenda.buraco[0] ?? '')} a ${diaCurto(dados.emenda.buraco[dados.emenda.buraco.length - 1] ?? '')}) sem pagamento para ninguém.`
                    : dados.emenda.sobreposicao.length > 0
                      ? `Esta semana se sobrepõe a uma já paga (${dados.emenda.sobreposicao.map((s) => semanaCurta(s.de, s.ate)).join(', ')}): esses dias já foram pagos uma vez.`
                      : dados.emenda.anterior
                        ? `Emenda com a semana paga ${semanaCurta(dados.emenda.anterior.de, dados.emenda.anterior.ate)}, sem buraco.`
                        : 'Nenhuma semana paga antes desta.'
                }
              />
              <Aviso
                ok={dados.avisos.devolucaoSemPessoa.length === 0}
                texto={
                  dados.avisos.devolucaoSemPessoa.length === 0
                    ? 'Toda devolução tem dono (código de quem vendeu).'
                    : `Devolução com código de vendedor sem pessoa: ${dados.avisos.devolucaoSemPessoa.map((d) => `cód ${d.codigo} (${String(d.itens)})`).join(', ')}.`
                }
              />
              {apiAtrasada && (
                <Aviso
                  ok={false}
                  texto={`A última leitura da API do HOS foi ${haQuanto(leitura)}. Confira o sincronizador em Integrações.`}
                />
              )}
              <li className="cf-regras-lista">
                <details>
                  <summary>As 12 regras da premiação</summary>
                  <ol>
                    {REGRAS_DA_PREMIACAO.map((r) => (
                      <li key={r.titulo}>
                        <strong>{r.titulo}</strong> — {r.efeito}
                      </li>
                    ))}
                  </ol>
                </details>
              </li>
              <li className="cf-info">
                {/* semPrograma e o R$ das devoluções são números da LOJA: só chegam para os donos
                    (null para os outros) — sem eles, o trecho some; zero seria número falso */}
                Fora da conta: {dados.foraDaConta.brindes} brindes (R$ 0,01),{' '}
                {dados.foraDaConta.semPrograma !== null &&
                  `${dados.foraDaConta.semPrograma.toLocaleString('pt-BR')} vendas de produto fora dos programas, `}
                {dados.foraDaConta.saldoZerado} vendas além do lote do programa (estoque normal) e{' '}
                {dados.foraDaConta.devolucaoForaPrograma.itens} devoluções fora dos programas
                {dados.foraDaConta.devolucaoForaPrograma.valor !== null &&
                  ` (${reais(dados.foraDaConta.devolucaoForaPrograma.valor)})`}
                .
              </li>
            </ul>
          </section>
        </div>
      )}

      {dados && pessoaAberta && (
        <DetalheDoVendedor
          c={pessoaAberta}
          semana={dados}
          semRelatorio={semRelatorio}
          sessao={sessao}
          podeCancelar={podeCancelar}
          podeEditar={podeConferir}
          aoMudar={() => carregar(true)}
          aoImprimir={() => setEscolher([pessoaAberta.pessoaId])}
          aoFechar={() => setAberto(null)}
        />
      )}

      {escolher && (
        <EscolherModelo
          quantas={escolher.length}
          aoFechar={() => setEscolher(null)}
          aoImprimir={(modelo) => {
            setImprimir({ ids: escolher, modelo });
            setEscolher(null);
          }}
        />
      )}
      {dados &&
        imprimir &&
        createPortal(
          <FolhasA4
            modelo={imprimir.modelo}
            pessoas={imprimir.ids.flatMap((id) => {
              const c = dados.pessoas.find((p) => p.pessoaId === id);
              return c ? [c] : [];
            })}
            semana={dados}
            semRelatorio={semRelatorio}
          />,
          document.body,
        )}
    </section>
  );
}

function Aviso({ ok, texto }: { ok: boolean; texto: string }) {
  return (
    <li className={ok ? 'cf-aviso-ok' : 'cf-aviso-alerta'}>
      <Icone nome={ok ? 'check' : 'warn'} tamanho={14} traco={1.8} />
      <span>{texto}</span>
    </li>
  );
}

/* ------------------------------------------------------------------ *
 * Tudo de um vendedor na semana
 * ------------------------------------------------------------------ */

function DetalheDoVendedor({
  c,
  semana,
  semRelatorio,
  sessao,
  podeCancelar,
  podeEditar,
  aoMudar,
  aoImprimir,
  aoFechar,
}: {
  c: ChequeDaPessoa;
  semana: ChequeDaSemana;
  semRelatorio: ReadonlySet<string>;
  sessao: Sessao;
  podeCancelar: boolean;
  podeEditar: boolean;
  aoMudar: () => void;
  aoImprimir: () => void;
  aoFechar: () => void;
}) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && aoFechar();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [aoFechar]);

  const pago = semana.pagamentos[c.pessoaId];

  return (
    <>
      <div className="pr-veu" onClick={aoFechar} aria-hidden="true" />
      <aside className="pr-gaveta cf-gaveta" role="dialog" aria-label={`Conferência de ${c.nome}`}>
        <div className="pr-gaveta-topo">
          <div className="pr-gaveta-pessoa">
            <span className="pr-avatar">
              <FotoDaConta
                accountId={c.pessoaId}
                nome={c.nome}
                semFoto={c.nome
                  .split(' ')
                  .map((x) => x[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase()}
              />
            </span>
            <div>
              <div className="pr-gaveta-nome">{c.nome}</div>
              <div className="pr-gaveta-sub">
                cód {c.codigo ?? '—'} · semana {semanaCurta(semana.periodo.de, semana.periodo.ate)}
                {pago && !pago.estornado ? ' · PAGA' : ''}
              </div>
            </div>
          </div>
          <button type="button" className="pr-fechar" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} traco={1.7} />
          </button>
        </div>
        <div className="pr-gaveta-corpo">
          <div className="cf-mini">
            {PROGRAMAS.map((x) => (
              <div key={x.id}>
                <span>{x.nome}</span>
                <strong>{reais(c.tipos[x.id].devido)}</strong>
              </div>
            ))}
            <div>
              <span>Canceladas</span>
              <strong className={c.cancelado ? 'pr-vermelho' : ''}>{reais(-c.cancelado)}</strong>
            </div>
            <div>
              <span>Devolução</span>
              <strong className={c.devolucao ? 'pr-vermelho' : ''}>{reais(-c.devolucao)}</strong>
            </div>
            <div>
              <span>Perdeu pelas regras</span>
              <strong className={c.perdeu ? 'pr-vermelho' : ''}>{reais(c.perdeu)}</strong>
            </div>
            <div className="cf-mini-total">
              <span>
                Premiação
                {c.meta > 0
                  ? ` · meta ${String(c.itensLiquidos)}/${String(c.meta)}${c.metaBatida ? ' ✓' : ' não batida'}`
                  : ''}
              </span>
              <strong>{reais(c.aReceber)}</strong>
            </div>
          </div>

          <VendasDaPessoa
            c={c}
            semRelatorio={semRelatorio}
            sessao={sessao}
            podeCancelar={podeCancelar}
            podeEditar={podeEditar}
            aoMudar={aoMudar}
          />
        </div>
        <div className="pr-gaveta-rodape">
          <button type="button" className="pr-botao-largo" onClick={aoImprimir}>
            <Icone nome="file" tamanho={13} traco={1.8} /> Imprimir folha
          </button>
          <a
            className="pr-botao-largo"
            href={linkDasVendas(c.pessoaId, semana.periodo.de, semana.periodo.ate, hojeEmMuriae())}
          >
            Ver os lançamentos
          </a>
          <a className="pr-botao-largo pr-forte" href={`#/premiacao?semana=${semana.periodo.de}`}>
            Ir para a premiação
          </a>
        </div>
      </aside>
    </>
  );
}
